/**
 * RENDER — speed FX & game-feel "juice".
 *
 * All cues are derived from GameState (velocity, position, status) and from course
 * GEOMETRY WE OWN (wall/booster/ramp volumes) — never from pointer/touch input and
 * never by computing steering. That boundary is deliberate: input belongs to Shell.
 *
 * Provides:
 *   - A full-screen speed vignette (radial streaks + edge darkening) whose intensity
 *     tracks speed, plus brief color flashes for near-miss (cyan) and boost (amber).
 *   - Near-miss detection: marble passing close to a wall at speed → cyan flash +
 *     a small camera micro-kick that resolves quickly.
 *   - Booster/shortcut detection: marble entering a booster/ramp volume → amber flash
 *     + FOV kick (handed to the camera by the renderer).
 *
 * The vignette is a single fullscreen triangle with a cheap fragment shader: no
 * post-processing pipeline, no extra render targets — friendly to mobile WebGL.
 * Everything decays critically (<200ms) so juice amplifies the read without smearing.
 */

import * as THREE from 'three';
import type { GameState, Vec3 } from '@/types';
import { FEEL, PALETTE } from './theme';
import { clamp, mapClamped, lenVec3 } from './math';
import type { BoxVolume } from './course';
import { disposeMaterial } from './three-util';

/** Output the renderer applies to the camera/marble after `update`. */
export interface FxFrame {
  /** Camera positional micro-kick (world units), already enveloped. */
  readonly kick: Vec3;
  /** Additive FOV kick in degrees, already enveloped. */
  readonly fovKick: number;
  /** Marble emissive flash color (0xRRGGBB), or -1 when no flash this frame. */
  readonly marbleFlashColor: number;
  /** Marble emissive intensity in [0,1+] this frame (0 = no flash). */
  readonly marbleFlashIntensity: number;
}

const FLASH_CYAN = new THREE.Color(PALETTE.cyan);
const FLASH_AMBER = new THREE.Color(PALETTE.booster);
const NO_COLOR = new THREE.Color(0x000000);

export class SpeedFx {
  /** Fullscreen overlay scene + ortho camera, drawn after the main pass. */
  private readonly overlayScene: THREE.Scene;
  private readonly overlayCamera: THREE.OrthographicCamera;
  private readonly quad: THREE.Mesh;
  private readonly geometry: THREE.BufferGeometry;
  private readonly material: THREE.ShaderMaterial;

  private readonly maxSpeed: number;

  // Flash envelopes (1 → 0), one per flavor.
  private nearMissT = 0;
  private boostT = 0;

  // Kick envelope and direction (world space), decays to zero.
  private kickT = 0;
  private readonly kickDir: [number, number, number] = [0, 0, 0];
  private readonly kickOut: [number, number, number] = [0, 0, 0];

  // Per-volume "inside" memory so a boost fires once on entry, not every frame.
  private readonly insideBoost = new Set<number>();

  // Scratch color for the blended flash.
  private readonly flashColor = new THREE.Color();

  constructor(maxSpeed: number) {
    this.maxSpeed = maxSpeed > 0 ? maxSpeed : 1;

    this.overlayScene = new THREE.Scene();
    this.overlayCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    // A single oversized clip-space triangle covering the screen (no UV seams).
    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(new Float32Array([-1, -1, 3, -1, -1, 3]), 2),
    );

    this.material = new THREE.ShaderMaterial({
      transparent: true,
      depthTest: false,
      depthWrite: false,
      uniforms: {
        uSpeed: { value: 0 },
        uFlash: { value: 0 },
        uFlashColor: { value: new THREE.Color(0x000000) },
        uAspect: { value: 1 },
      },
      vertexShader: VERT,
      fragmentShader: FRAG,
    });

    this.quad = new THREE.Mesh(this.geometry, this.material);
    this.quad.frustumCulled = false;
    this.overlayScene.add(this.quad);
  }

  setAspect(aspect: number): void {
    (this.material.uniforms.uAspect as THREE.IUniform).value = aspect;
  }

  /**
   * Advance FX for this frame.
   * @returns the enveloped camera kick + FOV kick for the renderer to apply.
   */
  update(
    state: GameState,
    marblePos: Vec3,
    walls: readonly BoxVolume[],
    boosts: readonly BoxVolume[],
    dt: number,
  ): FxFrame {
    const speed = lenVec3(state.velocity);
    const speedFrac = mapClamped(speed, 0, this.maxSpeed, 0, 1);

    // --- Near-miss: close to a wall while fast → cyan flash + micro-kick. ---
    if (speedFrac >= FEEL.nearMissMinSpeedFrac) {
      const hit = nearestWallGap(marblePos, walls);
      if (hit && hit.gap <= FEEL.nearMissGap) {
        // Only (re)trigger when the flash has mostly decayed, to avoid a solid glow
        // while skimming a long wall.
        if (this.nearMissT < 0.35) {
          this.nearMissT = 1;
          this.kickT = 1;
          // Kick AWAY from the wall, scaled by how fast we're going.
          const mag = FEEL.nearMissKick * (0.6 + 0.4 * speedFrac);
          this.kickDir[0] = hit.awayX * mag;
          this.kickDir[1] = 0;
          this.kickDir[2] = hit.awayZ * mag;
        }
      }
    }

    // --- Boost / shortcut: entering a booster or ramp volume → amber flash + FOV. ---
    let firedBoost = false;
    for (const v of boosts) {
      const inside = pointInBox(marblePos, v);
      if (inside && !this.insideBoost.has(v.id)) {
        this.insideBoost.add(v.id);
        firedBoost = true;
      } else if (!inside && this.insideBoost.has(v.id)) {
        this.insideBoost.delete(v.id);
      }
    }
    if (firedBoost) this.boostT = 1;

    // --- Decay envelopes (critical, framerate-independent enough at small dt). ---
    const decay = dt / Math.max(FEEL.flashDecay, 1e-3);
    this.nearMissT = clamp(this.nearMissT - decay, 0, 1);
    this.boostT = clamp(this.boostT - decay, 0, 1);
    this.kickT = clamp(this.kickT - decay, 0, 1);

    // --- Compose the screen flash color from whichever envelope is hotter. ---
    let flashAmt = 0;
    let flashHex = -1;
    this.flashColor.copy(NO_COLOR);
    if (this.boostT >= this.nearMissT) {
      flashAmt = this.boostT;
      if (flashAmt > 0) {
        this.flashColor.copy(FLASH_AMBER);
        flashHex = PALETTE.booster;
      }
    } else {
      flashAmt = this.nearMissT;
      if (flashAmt > 0) {
        this.flashColor.copy(FLASH_CYAN);
        flashHex = PALETTE.cyan;
      }
    }

    const u = this.material.uniforms;
    (u.uSpeed as THREE.IUniform).value = speedFrac;
    (u.uFlash as THREE.IUniform).value = flashAmt * 0.6; // cap so it never whites out
    (u.uFlashColor as THREE.IUniform).value = this.flashColor;

    // --- Output kick (eased) for the camera. ---
    const k = easeOutKick(this.kickT);
    this.kickOut[0] = this.kickDir[0] * k;
    this.kickOut[1] = this.kickDir[1] * k;
    this.kickOut[2] = this.kickDir[2] * k;
    const fovKick = easeOutKick(this.boostT) * FEEL.boostFovKick;

    return {
      kick: this.kickOut,
      fovKick,
      marbleFlashColor: flashHex,
      marbleFlashIntensity: flashAmt,
    };
  }

  /** Draw the overlay. Called by the renderer after the main scene, same frame. */
  render(renderer: THREE.WebGLRenderer): void {
    const prevAutoClear = renderer.autoClear;
    renderer.autoClear = false;
    renderer.render(this.overlayScene, this.overlayCamera);
    renderer.autoClear = prevAutoClear;
  }

  /** Reset transient state (e.g. on respawn/rebuild). */
  reset(): void {
    this.nearMissT = 0;
    this.boostT = 0;
    this.kickT = 0;
    this.insideBoost.clear();
  }

  dispose(): void {
    this.geometry.dispose();
    disposeMaterial(this.material);
    this.overlayScene.clear();
  }
}

/** A cubic ease-out so a kick punches then settles softly. */
function easeOutKick(t: number): number {
  const x = clamp(t, 0, 1);
  return x * x; // quick rise near 1, gentle tail near 0
}

interface WallHit {
  readonly gap: number;
  readonly awayX: number;
  readonly awayZ: number;
}

/**
 * Closest horizontal gap between the marble and any wall box (XZ plane). Returns the
 * gap and a normalized "away from wall" direction for the kick. Uses an AABB
 * distance in XZ — cheap and adequate for proximity FX.
 */
function nearestWallGap(p: Vec3, walls: readonly BoxVolume[]): WallHit | null {
  let best: WallHit | null = null;
  for (const w of walls) {
    const dx = p[0] - w.center[0];
    const dz = p[2] - w.center[2];
    // Distance from point to the box's nearest face in XZ.
    const ox = Math.abs(dx) - w.halfExtents[0];
    const oz = Math.abs(dz) - w.halfExtents[2];
    // Only consider when roughly alongside the wall (within its Z span + margin) or
    // its X span; we approximate gap as the larger outside component.
    const gap = Math.max(ox, oz);
    if (gap < 0) continue; // marble is overlapping/inside in XZ; not a "near miss"
    if (best === null || gap < best.gap) {
      // Away direction points from wall center toward the marble in the dominant axis.
      let ax = 0;
      let az = 0;
      if (ox >= oz) ax = dx >= 0 ? 1 : -1;
      else az = dz >= 0 ? 1 : -1;
      best = { gap, awayX: ax, awayZ: az };
    }
  }
  return best;
}

/** Axis-aligned point-in-box test (rotation ignored — fine for trigger-style FX). */
function pointInBox(p: Vec3, v: BoxVolume): boolean {
  return (
    Math.abs(p[0] - v.center[0]) <= v.halfExtents[0] &&
    Math.abs(p[1] - v.center[1]) <= v.halfExtents[1] &&
    Math.abs(p[2] - v.center[2]) <= v.halfExtents[2]
  );
}

const VERT = /* glsl */ `
  varying vec2 vUv;
  void main() {
    // position is a clip-space triangle; derive UV in [0,1] from clip coords.
    vUv = position.xy * 0.5 + 0.5;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

const FRAG = /* glsl */ `
  precision mediump float;
  varying vec2 vUv;
  uniform float uSpeed;       // 0..1
  uniform float uFlash;       // 0..~0.6
  uniform vec3  uFlashColor;
  uniform float uAspect;

  void main() {
    // Aspect-correct radial distance from center.
    vec2 c = vUv - 0.5;
    c.x *= uAspect;
    float r = length(c) * 1.4;

    // Speed vignette: edges darken & pick up a faint cool streak as speed rises.
    float vig = smoothstep(0.45, 1.05, r) * uSpeed;
    vec3 col = vec3(0.0);
    float a = vig * 0.55;

    // Radial speed streaks: cheap angular noise via the fragment's angle.
    float ang = atan(c.y, c.x);
    float streak = 0.5 + 0.5 * sin(ang * 60.0);
    a += streak * vig * 0.12;

    // Flash: additive color toward the edges so the center (track) stays readable.
    float edge = smoothstep(0.2, 1.1, r);
    col += uFlashColor * uFlash * edge;
    a = clamp(a + uFlash * edge * 0.8, 0.0, 1.0);

    gl_FragColor = vec4(col, a);
  }
`;
